import type { ProductCategoryTreeNode } from '@erp/shared';
import { ChevronDown, ChevronRight, FolderPlus, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  useArchiveProductCategory,
  useCreateProductCategory,
  useProductCategories,
  useUpdateProductCategory,
} from '@/features/products/api/use-product-categories';
import { cn } from '@/lib/utils';

interface ProductCategoryManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canWrite: boolean;
}

interface CategoryFormState {
  id: string | null;
  name: string;
  nameEn: string;
  description: string;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
}

const EMPTY_FORM: CategoryFormState = {
  id: null,
  name: '',
  nameEn: '',
  description: '',
  parentId: null,
  sortOrder: 0,
  isActive: true,
};

function formFor(category: ProductCategoryTreeNode): CategoryFormState {
  return {
    id: category.id,
    name: category.name,
    nameEn: category.nameEn ?? '',
    description: category.description ?? '',
    parentId: category.parentId,
    sortOrder: category.sortOrder,
    isActive: category.isActive,
  };
}

export function ProductCategoryManager({
  open,
  onOpenChange,
  canWrite,
}: ProductCategoryManagerProps): JSX.Element {
  const { t } = useTranslation();
  const categories = useProductCategories({ includeInactive: true });
  const create = useCreateProductCategory();
  const update = useUpdateProductCategory();
  const archive = useArchiveProductCategory();
  const [query, setQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<CategoryFormState>(EMPTY_FORM);
  const [archiveTarget, setArchiveTarget] = useState<ProductCategoryTreeNode | null>(null);

  useEffect(() => {
    if (categories.data !== undefined) {
      setExpanded(
        new Set(
          categories.data.filter((category) => category.hasChildren).map((category) => category.id),
        ),
      );
    }
  }, [categories.data]);

  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase();
    return (categories.data ?? []).filter((category) => {
      if (!includeInactive && !category.isActive) return false;
      if (needle.length > 0)
        return (
          category.name.toLocaleLowerCase().includes(needle) ||
          (category.nameEn?.toLocaleLowerCase().includes(needle) ?? false)
        );
      return category.path.slice(0, -1).every((ancestorId) => expanded.has(ancestorId));
    });
  }, [categories.data, expanded, includeInactive, query]);

  const startCreate = (parentId: string | null): void => {
    const siblings = (categories.data ?? []).filter((category) => category.parentId === parentId);
    setForm({
      ...EMPTY_FORM,
      parentId,
      sortOrder: Math.max(-1, ...siblings.map((category) => category.sortOrder)) + 1,
    });
  };

  const save = (): void => {
    const input = {
      name: form.name,
      nameEn: form.nameEn.trim() || null,
      description: form.description.trim() || null,
      parentId: form.parentId,
      sortOrder: form.sortOrder,
      isActive: form.isActive,
    };
    if (form.id === null) {
      create.mutate(input, {
        onSuccess: (saved) => {
          setForm(formFor({ ...saved, depth: 0, path: [saved.id], hasChildren: false }));
          toast.success(t('products.categories.created'));
        },
        onError: (error) =>
          toast.error(t('products.categories.saveError'), { description: error.message }),
      });
    } else {
      update.mutate(
        { id: form.id, input },
        {
          onSuccess: () => toast.success(t('products.categories.updated')),
          onError: (error) =>
            toast.error(t('products.categories.saveError'), { description: error.message }),
        },
      );
    }
  };

  const isPending = create.isPending || update.isPending || archive.isPending;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent closeLabel={t('common.close')} className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>{t('products.categories.title')}</DialogTitle>
            <DialogDescription>{t('products.categories.description')}</DialogDescription>
          </DialogHeader>
          <div className="grid min-h-[30rem] gap-4 lg:grid-cols-[minmax(18rem,1fr)_minmax(22rem,1fr)]">
            <section
              className="space-y-3 rounded-lg border border-border p-3"
              aria-label={t('products.categories.tree')}
            >
              <div className="flex gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    className="pl-9"
                    placeholder={t('products.categories.search')}
                    aria-label={t('products.categories.search')}
                  />
                </div>
                {canWrite ? (
                  <Button
                    size="icon"
                    variant="outline"
                    aria-label={t('products.categories.createRoot')}
                    title={t('products.categories.createRoot')}
                    onClick={() => startCreate(null)}
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  </Button>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="include-inactive-categories"
                  checked={includeInactive}
                  onCheckedChange={setIncludeInactive}
                />
                <Label htmlFor="include-inactive-categories" className="font-normal">
                  {t('products.categories.showInactive')}
                </Label>
              </div>
              {categories.isPending ? (
                <div className="space-y-2" aria-busy="true">
                  {Array.from({ length: 7 }, (_, index) => (
                    <Skeleton key={index} className="h-11 w-full" />
                  ))}
                </div>
              ) : categories.isError ? (
                <div className="space-y-3 p-4 text-center">
                  <p className="text-sm text-destructive">{t('products.categories.loadError')}</p>
                  <Button variant="outline" onClick={() => void categories.refetch()}>
                    {t('common.retry')}
                  </Button>
                </div>
              ) : rows.length === 0 ? (
                <div className="space-y-3 p-8 text-center">
                  <p className="text-sm text-muted-foreground">{t('products.categories.empty')}</p>
                  {canWrite ? (
                    <Button onClick={() => startCreate(null)}>
                      <Plus className="h-4 w-4" aria-hidden="true" />
                      {t('products.categories.createRoot')}
                    </Button>
                  ) : null}
                </div>
              ) : (
                <div className="max-h-[28rem] space-y-1 overflow-y-auto">
                  {rows.map((category) => (
                    <div
                      key={category.id}
                      className={cn(
                        'flex min-h-11 items-center gap-1 rounded-md pr-1',
                        form.id === category.id ? 'bg-accent' : 'hover:bg-muted/60',
                      )}
                      style={{ paddingLeft: `${String(category.depth * 16 + 4)}px` }}
                    >
                      <button
                        type="button"
                        className="flex h-11 w-8 items-center justify-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        disabled={!category.hasChildren}
                        aria-label={
                          category.hasChildren
                            ? t(
                                expanded.has(category.id)
                                  ? 'products.categories.collapse'
                                  : 'products.categories.expand',
                                { name: category.name },
                              )
                            : undefined
                        }
                        onClick={() =>
                          setExpanded((current) => {
                            const next = new Set(current);
                            if (next.has(category.id)) next.delete(category.id);
                            else next.add(category.id);
                            return next;
                          })
                        }
                      >
                        {category.hasChildren ? (
                          expanded.has(category.id) ? (
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                          ) : (
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          )
                        ) : null}
                      </button>
                      <button
                        type="button"
                        className="min-w-0 flex-1 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => setForm(formFor(category))}
                      >
                        <span className="block truncate text-sm font-medium">{category.name}</span>
                        <span className="block text-xs text-muted-foreground">
                          {t('products.categories.productCount', { count: category.productCount })}
                        </span>
                      </button>
                      {!category.isActive ? (
                        <Badge variant="secondary">{t('products.categories.inactive')}</Badge>
                      ) : null}
                      {canWrite ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label={t('products.categories.createChild', { name: category.name })}
                          title={t('products.categories.createChild', { name: category.name })}
                          onClick={() => startCreate(category.id)}
                        >
                          <FolderPlus className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section
              className="space-y-4 rounded-lg border border-border p-4"
              aria-label={t('products.categories.editor')}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold">
                  {form.id === null
                    ? t('products.categories.createTitle')
                    : t('products.categories.editTitle')}
                </h3>
                {form.id === null ? null : (
                  <Badge variant="outline">
                    <Pencil className="h-3 w-3" aria-hidden="true" />
                    {t('products.categories.productCount', {
                      count:
                        categories.data?.find((category) => category.id === form.id)
                          ?.productCount ?? 0,
                    })}
                  </Badge>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="category-name">{t('products.categories.name')}</Label>
                <Input
                  id="category-name"
                  value={form.name}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, name: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="category-name-en">{t('products.categories.nameEn')}</Label>
                <Input
                  id="category-name-en"
                  value={form.nameEn}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, nameEn: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="category-description">
                  {t('products.categories.categoryDescription')}
                </Label>
                <Textarea
                  id="category-description"
                  value={form.description}
                  disabled={!canWrite}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, description: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="category-parent">{t('products.categories.parent')}</Label>
                <Select
                  value={form.parentId ?? 'ROOT'}
                  disabled={!canWrite}
                  onValueChange={(value) =>
                    setForm((current) => ({
                      ...current,
                      parentId: value === 'ROOT' ? null : value,
                    }))
                  }
                >
                  <SelectTrigger id="category-parent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ROOT">{t('products.categories.root')}</SelectItem>
                    {(categories.data ?? [])
                      .filter((category) => category.id !== form.id)
                      .map((category) => (
                        <SelectItem
                          key={category.id}
                          value={category.id}
                        >{`${'· '.repeat(category.depth)}${category.name}`}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="category-sort">{t('products.categories.sortOrder')}</Label>
                  <Input
                    id="category-sort"
                    type="number"
                    min={0}
                    value={form.sortOrder}
                    disabled={!canWrite}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, sortOrder: Number(event.target.value) }))
                    }
                  />
                </div>
                <div className="flex items-end gap-2 pb-2">
                  <Switch
                    id="category-active"
                    checked={form.isActive}
                    disabled={!canWrite}
                    onCheckedChange={(checked) =>
                      setForm((current) => ({ ...current, isActive: checked }))
                    }
                  />
                  <Label htmlFor="category-active" className="font-normal">
                    {t('products.categories.active')}
                  </Label>
                </div>
              </div>
              {canWrite ? (
                <DialogFooter className="border-t border-border pt-4">
                  <Button
                    variant="outline"
                    disabled={form.id === null || isPending}
                    onClick={() => {
                      if (form.id === null) return;
                      setArchiveTarget(
                        categories.data?.find((category) => category.id === form.id) ?? null,
                      );
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    {t('products.categories.archive')}
                  </Button>
                  <Button disabled={form.name.trim().length === 0 || isPending} onClick={save}>
                    {form.id === null ? (
                      <Plus className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    )}
                    {t('common.save')}
                  </Button>
                </DialogFooter>
              ) : null}
            </section>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={archiveTarget !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setArchiveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>{t('products.categories.archiveTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('products.categories.archiveDescription', { name: archiveTarget?.name ?? '' })}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiveTarget === null || archive.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (archiveTarget === null) return;
                archive.mutate(archiveTarget.id, {
                  onSuccess: () => {
                    setArchiveTarget(null);
                    setForm(EMPTY_FORM);
                    toast.success(t('products.categories.archived'));
                  },
                  onError: (error) =>
                    toast.error(t('products.categories.archiveError'), {
                      description: error.message,
                    }),
                });
              }}
            >
              {t('products.categories.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
